import React, { useRef, useState } from 'react';

/**
 * Reusable Image Upload Field with Drag & Drop Support
 * Used across all admin image input fields
 */
function ImageUploadField({
  label = "Image",
  imageFile,
  preview,
  fileInputRef,
  onFileChange,
  onClear,
  validation,
  error,
  accept = "image/jpeg,image/png,image/webp,image/gif,image/svg+xml,image/bmp,image/tiff,image/x-icon,image/heic,image/heif,image/avif",
  requirements = "Max: 5MB (Common Image Formats)"
}) {
  const [dragActive, setDragActive] = useState(false);
  const dropZoneRef = useRef();

  // Handle drag events
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  // Handle drop
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      // Validate file is an image
      if (file.type.startsWith('image/')) {
        // Create a synthetic event to match the input onChange handler
        const event = {
          target: { files: [file] }
        };
        onFileChange(event);
      }
    }
  };

  return (
    <div className="km-field km-field-full">
      <label className="km-label">
        {label} • {requirements}
      </label>
      <div className="upload-grid-wrapper">
        <div
          ref={dropZoneRef}
          className={`drop-zone-area ${imageFile ? "active-file" : ""} ${dragActive ? "drag-active" : ""}`}
          onClick={() => fileInputRef?.current?.click()}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          style={{
            transition: 'all 0.2s ease',
            backgroundColor: dragActive ? 'rgba(24, 26, 46, 0.08)' : undefined,
            borderColor: dragActive ? '#299d5a' : undefined,
            borderWidth: dragActive ? '2px' : undefined,
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={accept}
            style={{ display: "none" }}
            onChange={onFileChange}
          />
          <div className="drop-zone-info">
            <div className="upload-icon text-center">
              {dragActive ? "📥" : imageFile ? "✅" : "📸"}
            </div>
            <p className="upload-text">
              {dragActive ? (
                <>Drop image here</>
              ) : imageFile ? (
                <b>{imageFile.name}</b>
              ) : (
                <>
                  Click to <b>browse</b> or <b>drag & drop</b>
                </>
              )}
            </p>
          </div>
        </div>

        {preview && (
          <div className="preview-tile fade-in">
            <img src={preview} alt="Preview" />
            <button
              type="button"
              className="preview-remove"
              onClick={onClear}
              title="Remove image"
            >
              ✕
            </button>
          </div>
        )}
      </div>

      {((validation && !validation.valid) || error) && (
        <div style={{ color: "#ef4444", fontSize: 12, marginTop: 8, fontWeight: 500 }}>
          ⚠ {(!validation?.valid && validation?.error) || error}
        </div>
      )}

      <style>{`
        .upload-grid-wrapper {
          display: flex;
          gap: 16px;
          margin-top: 8px;
          align-items: flex-start;
        }
        .drop-zone-area {
          flex: 1;
          height: 110px;
          border: 2px dashed rgba(0, 0, 0, 0.1);
          background: rgba(0, 0, 0, 0.02);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        .drop-zone-area:hover {
          border-color: #299d5a;
          background: rgba(72, 127, 255, 0.04);
        }
        .drop-zone-area.active-file {
          border-color: #45b369;
          background: rgba(69, 179, 105, 0.05);
        }
        .drop-zone-info {
          text-align: center;
        }
        .upload-icon {
          text-align: center;
          font-size: 20px;
          margin-bottom: 4px;
        }
        .upload-text {
          font-size: 13px;
          color: #666;
          margin: 0;
          text-align: center;
        }
        .preview-tile {
          width: 110px;
          height: 110px;
          border-radius: 12px;
          overflow: hidden;
          position: relative;
          border: 2px solid #299d5a;
          flex-shrink: 0;
        }
        .preview-tile img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .preview-remove {
          position: absolute;
          top: 5px;
          right: 5px;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: rgba(0, 0, 0, 0.7);
          color: white;
          border: none;
          cursor: pointer;
          font-size: 11px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .preview-remove:hover {
          background: rgba(239, 68, 68, 0.9);
        }
      `}</style>
    </div>
  );
}

export default ImageUploadField;
